const DB_NAME = "cardex-drafts-db";
const DB_VERSION = 1;
const STORE_NAME = "images";

export interface DraftImageMeta {
  name: string;
  type: string;
  lastModified: number;
}

interface DraftImageRecord {
  key: string;
  blob: Blob;
  meta: DraftImageMeta;
  updatedAt: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("Failed to open IndexedDB"));
  });
}

export async function saveDraftImage(
  key: string,
  blob: Blob,
  meta: DraftImageMeta
): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error("Failed to save draft image"));
    tx.objectStore(STORE_NAME).put({
      key,
      blob,
      meta,
      updatedAt: Date.now(),
    } as DraftImageRecord);
  });
  db.close();
}

export async function getDraftImage(
  key: string
): Promise<DraftImageRecord | null> {
  const db = await openDb();
  const result = await new Promise<DraftImageRecord | null>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(key);
    req.onsuccess = () => resolve((req.result as DraftImageRecord) || null);
    req.onerror = () => reject(req.error || new Error("Failed to read draft image"));
  });
  db.close();
  return result;
}

export async function deleteDraftImage(key: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error("Failed to delete draft image"));
    tx.objectStore(STORE_NAME).delete(key);
  });
  db.close();
}
